import cache from '../shared/cache';
export class LocalQuote {
    static WILDCARD = 'none';

    // In-memory map for any “pending” promise that’s not yet saved to localStorage
    static _inMemoryPromises = new Map();

    /**
     * Return the quote for the given cartId.
     * @param {*} cartId 
     * @returns {Promise<Object|null>} A promise that resolves to the quote object or null if not found.
     */
    static get = async (cartId) => {
        if (cartId === this.WILDCARD) {
            return null;
        }

        // Is there a pending promise?
        if (this._inMemoryPromises.has(cartId)) {
            return this._inMemoryPromises.get(cartId);
        }
        

        // Otherwise, try to load the already-resolved object from localStorage
        const quotesMap = cache.get('os_local_quotes') ?? {};

        if (!quotesMap[cartId]) return null;

        if (quotesMap[cartId].responseWaiting) { 
            // If the quote is still waiting for a response and is not in local memory it means we never actually received it
            // Invalidate cache have the server send it again
            this.remove(cartId);
            return null;
        }


        return quotesMap[cartId].value;
    };

    /**
     * Check if a quote exists for the given cartId.
     */
    static has = (cartId) => {
        if (cartId === this.WILDCARD) return false;

        if (this._inMemoryPromises.has(cartId)) {
            return true;
        }

        const quotesMap = cache.get('os_local_quotes') ?? {};
        return Object.prototype.hasOwnProperty.call(quotesMap, cartId);
    };

    /**
     * 
     * @param {*} cartId The cart ID for which to set the quote.
     * @param {*} quote The Quote object or a Promise that resolves to the quote.
     * @returns 
     */
    static set = (cartId, quote) => {
        if (cartId === this.WILDCARD) return null;

        // If it’s a Promise: keep it in the in-memory map only
        if (quote instanceof Promise) {
            this._inMemoryPromises.set(cartId, quote);
            // update this quote in localStorage to indicate that it’s waiting for a response
            const quotesMap = cache.get('os_local_quotes') ?? {};
            quotesMap[cartId].responseWaiting = true;
            cache.set('os_local_quotes', quotesMap);
            return quote;
        }

        // Otherwise, it’s (presumably) a plain object -> remove in-memory entry and write to localStorage
        if (this._inMemoryPromises.has(cartId)) {
            this._inMemoryPromises.delete(cartId);
        }

        // Grab whatever was in localStorage (or default to {})
        const quotesMap = cache.get('os_local_quotes') ?? {};

        if (!quotesMap[cartId]) {
          quotesMap[cartId] = {};
        }

        quotesMap[cartId].value = quote;
        quotesMap[cartId].responseWaiting = false;

        cache.set('os_local_quotes', quotesMap);

        return quote;
    };

    /**
     * Remove both any pending promise and any stored data
     */
    static remove = (cartId) => {
        if (this._inMemoryPromises.has(cartId)) {
            this._inMemoryPromises.delete(cartId);
        }

        const quotesMap = cache.get('os_local_quotes') ?? {};
        delete quotesMap[cartId];
        cache.set('os_local_quotes', quotesMap);

        return quotesMap;
    };
}
